import React, { useState, useEffect } from 'react';
import useApi from '../../hooks/useApi';
import Popup from '../../components/Popup';
import ErrorMessage from '../../components/ErrorMessage';
import SecondaryButton from '../../components/SecondaryButton';
import WizardButton from '../../components/WizardButton';
import PrimaryButton from '../../components/PrimaryButton';
import CheckBox from '../../components/CheckBox';
import RadioButton from '../../components/RadioButton';
import DatePicker from '../../components/DatePicker';
import {
  DateWrapper,
  Wrapper,
  ColumnWrapper,
  ContentPanel,
  SessionNameWrapper,
  SessionName,
  OrganizationInfo,
  ButtonPanel,
  SettingsPanel,
  SessionSetting,
  RadioButtonWrapper,
  DateDivider,
  SettingsHeader,
} from './styles';

const SessionSettingsPopup = ({
  popupIsOpen,
  onClose,
  onChangePage,
  userToken,
  loopId,
  loopName,
  organization,
  flash,
  session,
  isNew,
  payload,
}) => {
  const { name = '', certificate = {} } = organization || {};
  const { certification_enabled, organisation_id, state } = session || {};
  const [showOrganisationText, setShowOrganisationText] = useState(true);
  const [sessionName, setSessionName] = useState(isNew ? loopName : session.name);
  const [organizationName, setOrganizationName] = useState(name);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSessionPeriodEnabled, setIsSessionPeriodEnabled] = useState(false);
  const [isSessionExitEnabled, setIsSessionExitEnabled] = useState(false);
  const [isSessionCertificationEnabled, setIsSessionCertificationEnabled] = useState(
    isNew ? !!(certificate || {}).enabled : !!certification_enabled,
  );
  const [sessionExitSelection, setSessionExitSelection] = useState('');
  const [sessionStartDate, setSessionStartDate] = useState(null);
  const [sessionEndDate, setSessionEndDate] = useState(null);
  const { getData, postData, putData, loading } = useApi(userToken);
  const [loadData, setLoadData] = useState(true);

  const infoKey = isNew ? 'session_create_connected_organisation' : 'session_edit_connected_organisation';

  useEffect(() => {
    const getSession = async () => {
      const response = await getData(`/session/${session.uuid}/edit`);
      if (response.success) {
        const { userOrganisationCount, organisation, start_at_iso, stop_at_iso, allow_leaving, name } = response.body;
        setSessionName(name);
        setShowOrganisationText(userOrganisationCount > 1);
        setOrganizationName(organisation.name);
        setSessionStartDate(start_at_iso);
        setSessionEndDate(stop_at_iso);
        setIsSessionPeriodEnabled(!!(start_at_iso || stop_at_iso));
        setSessionExitSelection(allow_leaving);
        setIsSessionExitEnabled(!!allow_leaving);
        setIsSessionCertificationEnabled(!!session.certification_enabled);
      } else {
        flash(i18n.__('app_error_alert'));
      }
    };
    const getSessionContext = async () => {
      const response = await getData(`/organisation/circle/${loopId}/session_context/settings`);
      if (response.success) {
        const { start_at_iso, stop_at_iso, allow_leaving } = payload;
        const { userOrganisationCount } = response.body;
        setShowOrganisationText(userOrganisationCount > 1);
        setSessionStartDate(start_at_iso);
        setSessionEndDate(stop_at_iso);
        setIsSessionPeriodEnabled(!!(start_at_iso || stop_at_iso));
        setSessionExitSelection(allow_leaving);
        setIsSessionExitEnabled(!!allow_leaving);
      } else {
        flash(i18n.__('app_error_alert'));
      }
    };
    if (popupIsOpen && loadData) {
      if (isNew) {
        getSessionContext();
      } else {
        getSession();
      }
      setLoadData(false);
    }
  }, [popupIsOpen, loadData]);

  useEffect(() => {
    if (sessionName) {
      setErrorMessage('');
    }
  }, [sessionName]);

  const getPayload = (activate) => {
    let newPayload = {
      ...payload,
      name: sessionName,
      organisation_id: isNew ? organization.id : organisation_id,
      update_settings: true,
      activate,
    };
    newPayload = { ...newPayload, certification_enabled: isSessionCertificationEnabled };
    if (isSessionExitEnabled) {
      newPayload = { ...newPayload, allow_leaving: sessionExitSelection };
    } else {
      delete newPayload.allow_leaving;
    }
    if (isSessionPeriodEnabled) {
      newPayload = { ...newPayload, start_date: sessionStartDate, end_date: sessionEndDate };
    } else {
      delete newPayload.start_date;
      delete newPayload.end_date;
    }
    return newPayload;
  };

  const saveSession = async (activate) => {
    if (!sessionName) {
      setErrorMessage(i18n.__('sessionNameRequired'));
    } else {
      const newPayload = { ...getPayload(), activate };
      const response = await (isNew
        ? postData(`/session/${loopId}`, newPayload)
        : putData(`/session/${session.uuid}`, newPayload));
      if (response.redirect) {
        onClose(response.url);
      } else if (response.success) {
        handleClose();
        window.location.reload();
      } else {
        if (response.code === 401) {
          flash(i18n.__('app_error_unauthorized'), { ttl: 5000 });
        } else {
          flash(i18n.__('app_error_alert'));
        }
      }
    }
  };

  const goNext = () => {
    const newPayload = getPayload();
    onChangePage('participants', newPayload);
  };

  const handleClose = () => {
    setLoadData(true);
    setSessionName(isNew ? loopName : session.name);
    onClose();
  };

  const radioButton = (value) => (
    <RadioButtonWrapper isEnabled={isSessionExitEnabled}>
      <RadioButton
        key={`${value}_${isSessionExitEnabled}`}
        name="sessionExitSelection"
        value={value}
        disabled={!isSessionExitEnabled}
        text={i18n.__(`session_create_allow_leaving_${value}`)}
        defaultChecked={sessionExitSelection === value}
        onClick={() => {
          setSessionExitSelection(value);
        }}
      />
    </RadioButtonWrapper>
  );

  const contentPanel = (
    <ContentPanel>
      <SessionNameWrapper>
        <SessionName
          id="sessionName"
          aria-required={true}
          label={i18n.__('session_create_name_placeholder')}
          placeholder={i18n.__('session_create_name_placeholder')}
          defaultValue={sessionName}
          onChange={(ev) => {
            setSessionName(ev.target.value.trim());
          }}
        >
          {errorMessage && <ErrorMessage message={errorMessage} />}
        </SessionName>
      </SessionNameWrapper>
      {showOrganisationText && (
        <OrganizationInfo dangerouslySetInnerHTML={{ __html: i18n.__(infoKey, organizationName) }} />
      )}
      <SettingsPanel>
        <SettingsHeader>{i18n.__('session_settings')}</SettingsHeader>
        <SessionSetting>
          <CheckBox
            name="sessionCertification"
            value={1}
            text={i18n.__('session_certification')}
            defaultChecked={isSessionCertificationEnabled}
            onChange={(ev) => {
              const checked = ev.target.checked;
              setIsSessionCertificationEnabled(checked);
            }}
          />
        </SessionSetting>
        <SessionSetting>
          <CheckBox
            name="sessionPeriod"
            value={1}
            text={i18n.__('session_create_set_deadline')}
            defaultChecked={isSessionPeriodEnabled}
            onChange={(ev) => {
              const checked = ev.target.checked;
              setIsSessionPeriodEnabled(checked);
              setSessionStartDate(checked ? sessionStartDate : null);
              setSessionEndDate(checked ? sessionEndDate : null);
            }}
          />
          <DateWrapper>
            <DatePicker
              disabled={!isSessionPeriodEnabled}
              value={sessionStartDate}
              onChange={setSessionStartDate}
              maxDate={sessionEndDate}
              key={Date.now()}
            />
            <DateDivider disabled={!isSessionPeriodEnabled}>—</DateDivider>
            <DatePicker
              disabled={!isSessionPeriodEnabled}
              value={sessionEndDate}
              onChange={setSessionEndDate}
              minDate={sessionStartDate}
              key={Date.now() + 1}
            />
          </DateWrapper>
        </SessionSetting>
        <SessionSetting>
          <CheckBox
            name="sessionExit"
            value={1}
            text={i18n.__('session_create_allow_leaving')}
            defaultChecked={isSessionExitEnabled}
            onChange={(ev) => {
              const checked = ev.target.checked;
              setIsSessionExitEnabled(checked);
              setSessionExitSelection(checked ? 'done' : '');
            }}
          />
          <ColumnWrapper>
            {radioButton('always')}
            {radioButton('done')}
            {radioButton('accepted')}
          </ColumnWrapper>
        </SessionSetting>
      </SettingsPanel>
    </ContentPanel>
  );

  const buttonPanel = (
    <ButtonPanel isNew={isNew}>
      {isNew ? (
        <>
          <Wrapper>
            <PrimaryButton text={i18n.__('session_start_now')} onClick={() => saveSession(true)} disabled={loading} />
            <PrimaryButton
              className="start-later"
              text={i18n.__('session_start_later')}
              onClick={() => saveSession(false)}
              disabled={loading}
            />
          </Wrapper>
          <WizardButton text={i18n.__('session_invite_students')} onClick={goNext} disabled={loading} />
        </>
      ) : (
        <SecondaryButton
          text={i18n.__('app_update')}
          onClick={() => saveSession(state === 'active')}
          disabled={loading}
        />
      )}
    </ButtonPanel>
  );

  return (
    <Popup
      width={600}
      popupIsOpen={popupIsOpen}
      id="startSessionPopup"
      heading={isNew ? i18n.__('session_create_header') : i18n.__('session_edit_header')}
      contentPanel={contentPanel}
      buttonPanel={buttonPanel}
      onClose={handleClose}
    />
  );
};

export default SessionSettingsPopup;
